<script lang="ts">
  import {
    createColumnHelper,
    createSvelteTable,
    getCoreRowModel,
    getExpandedRowModel,
    type ColumnDefTemplate,
    type CellContext,
    type Row,
    type Cell,
    type Header,
    type Updater,
    type Column,
    getSortedRowModel,
    type ColumnSort,
  } from "@tanstack/svelte-table";
  import { createGroupedTransactionsQuery } from "../../queries/transactions";
  import { filteringDate } from "../../helpers/store";
  import TextNew from "../TextNew.svelte";
  import { format, isSameDay, isSameMonth, isSameYear, startOfDay } from "date-fns";
  import ButtonNew from "../buttons/ButtonNew.svelte";
  import {
    type GroupedTransaction,
    type GroupedTransactions,
    type Transaction,
  } from "../../api/transactions";
  import { globalDatePickerStore } from "../../stores/date-picker";
  import DateCell from "./DateCell.svelte";
  import ExclamationCircleIcon from "../icons/ExclamationCircleIcon.svelte";
  import { onMount, beforeUpdate, afterUpdate, onDestroy } from "svelte";
  import { linkTransactionsModal } from "../../stores/link-transactions-modal-store";
  import { createDropdownMenu } from "../dropdown-menu/create";
  import ContextMenu from "./ContextMenu.svelte";
  import FilterMenuItem from "./FilterMenuItem.svelte";
  import { derived, get, writable } from "svelte/store";
  import Icon from "../icons/Icon.svelte";
  import { difference, isEmpty } from "lodash";
  import {
    COL_HEADER_HEIGHT,
    GRID_WIDTH,
    INVOICE_COLUMN_FILTER_STORAGE_KEY,
    SCROLL_BAR_WIDTH,
    displayedTransactionTypes,
    MIN_COL_WIDTH_CREDIT_NOTE_TYPE,
    MIN_COL_WIDTH_CREDIT_REQUEST_TYPE,
    FILTER_MENU_MIN_WIDTH,
    MAX_TABLE_PAGINATION_CONTROL_HEIGHT,
    CHAT_FILTER_STORAGE_KEY,
    CHAT_FILTER_PARAM_KEY,
    CHAT_FILTER_VERSION,
    INVOICE_COLUMN_FILTER_VERSION,
    DOCUMENT_TYPE_FILTER_PARAM_KEY,
    DOCUMENT_TYPE_FILTER_STORAGE_KEY,
    DOCUMENT_TYPE_FILTER_VERSION,
  } from "./constants";
  import SkeletonColHeader from "./SkeletonColHeader.svelte";
  import InvoiceRowSkeleton from "./InvoiceRowSkeleton.svelte";
  import HeaderCell from "./HeaderCell.svelte";
  import { tableOptions } from "../../helpers/table";
  import { createPersistedLocalStore } from "../../stores/persisted-local-store";
  import { querystring, push, location, replace } from "svelte-spa-router";
  import EmptyStateBasic from "../empty-states/EmptyStateBasic.svelte";
  import ListIcon from "../icons/ListIcon.svelte";
  import TransactionStatus, { type SecondaryStatus } from "./TransactionStatus.svelte";
  import { sideMenuWidth } from "../side-menu/SideMenu.svelte";
  import { __DEV__ } from "../../helpers/constants";
  import { invoicesPageHeaderHeight, invoicesPageOverviewHeight } from "./stores/dimensions";
  import AccountingIntegrationStatus, {
    isValidAccountingIntegrationStatus,
  } from "./AccountingIntegrationStatus.svelte";
  import { basicTooltip } from "../../actions/tooltip";
  import { showAsUnlinkedTransaction } from "./transactions/data";
  import FileOutlineIcon from "../icons/FileOutlineIcon.svelte";
  import { extractLinkStatusParam } from "../../helpers/url";
  import HeaderCellFilter from "./HeaderCellFilter.svelte";
  import { currencyFormatter } from "../../helpers/localise";
  import { t } from "../../helpers/translations";
  import {
    createChatFilterMenuItems,
    syncQueryParamsWithChatFilters,
  } from "../dropdown-menu/chat-filter-menu";
  import { createSuppliersChatsByBuyerIDsQuery } from "../../queries/suppliers";
  import BodyCell, { type ExpandableEdge } from "./BodyCell.svelte";
  import ExpandableToggle from "./ExpandableToggle.svelte";
  import { selectedBuyer } from "../../stores/buyer";
  import {
    createDocumentTypeFilterMenuItems,
    syncQueryParmsWithDocumentTypeFilters,
  } from "../dropdown-menu/document-type-filter-menu";
  import { dateInTZ } from "../../helpers/utils";
  import { supplierTimezone } from "../../helpers/landing";

  type IndexRange = [number, number];

  type EnrichedGroupedTransaction = GroupedTransaction & {
    isLastSubRowInGroup?: boolean;
  };

  const {
    open: isFilterMenuOpen,
    menu,
    onTriggerClick,
    menuAttrs,
    triggerAttrs,
    triggerElement,
  } = createDropdownMenu();

  const {
    open: supplierFilterOpen,
    menu: supplierFilterMenu,
    menuAttrs: supplierFilterMenuAttrs,
    triggerAttrs: supplierFilterTriggerAttrs,
    onTriggerClick: supplierFilterOnTriggerClick,
    triggerElement: supplierFilterTriggerElement,
  } = createDropdownMenu();

  const {
    open: documentTypeFilterOpen,
    menu: documentTypeFilterMenu,
    menuAttrs: documentTypeFilterMenuAttrs,
    triggerAttrs: documentTypeFilterTriggerAttrs,
    onTriggerClick: documentTypeFilterOnTriggerClick,
    triggerElement: documentTypeFilterTriggerElement,
  } = createDropdownMenu();

  let supplierNameColWidth = 0;
  let hasInitializedFilters = false;
  let isSupplierTooltipOpen = false;
  let supplierTooltipTimeout: NodeJS.Timeout | null = null;
  let initialPageLoadTimeout: NodeJS.Timeout | null = null;
  let expansionButtonHoveredByRowID = new Map<string, boolean>();
  let hasInitialPageLoaded = false;

  const supplierFilterBadgeElement = writable<HTMLElement | null>(null);
  const documentTypeFilterBadgeElement = writable<HTMLElement | null>(null);
  $: dateColWidth = 0;

  const columnHelper = createColumnHelper<EnrichedGroupedTransaction>();

  function formatPrice<T extends CellContext<EnrichedGroupedTransaction, number>>(context: T) {
    if (context.row.original.amount === "--") {
      return "";
    }
    return $currencyFormatter.format(context.getValue() / 100);
  }

  // please update exported cols https://github.com/rekki/go/blob/master/cmd/insights/pkg/invoices/export.go#L312
  // if you make any changes here.
  // Everything should be included in the export (except for the filter display column)
  const defaultColumns = [
    columnHelper.accessor("delivery_date", {
      cell: (context) => {
        const deliveryDate = context.getValue();
        if (!deliveryDate) {
          return "";
        }
        return format(
          dateInTZ(deliveryDate, supplierTimezone(context.row.original.supplier_id)),
          "d EEE"
        );
      },
      header: "date",
      id: "date",
      sortingFn: "datetime",
      enableSorting: true,
    }),
    columnHelper.accessor("supplier_name", {
      cell: (context) => context.getValue(),
      header: "supplier",
      id: "supplier_name",
      enableSorting: false,
    }),
    columnHelper.accessor("type", {
      cell: (context) => {
        const type = context.getValue();
        return displayedTransactionTypes[type] ?? type;
      },
      header: "type",
      id: "type",
      enableSorting: false,
    }),
    columnHelper.accessor("delivery_date", {
      cell: (context) => {
        const deliveryDate = context.getValue();
        if (!deliveryDate) {
          return "";
        }
        return format(
          dateInTZ(deliveryDate, supplierTimezone(context.row.original.supplier_id)),
          "EEEE, dd"
        );
      },
      header: "delivery date",
      id: "delivery_date",
      sortingFn: "datetime",
      enableSorting: true,
    }),
    columnHelper.accessor("processed_date", {
      cell: (context) => {
        const deliveryDate = context.getValue();
        if (!deliveryDate) {
          return "";
        }
        return format(new Date(deliveryDate), "EEEE, dd");
      },
      header: "processed date",
      id: "processed_date",
      sortingFn: "datetime",
      enableSorting: true,
    }),
    columnHelper.accessor("invoice_number", {
      cell: (context) => context.getValue(),
      header: "invoice number",
      id: "invoice_number",
      enableSorting: true,
    }),
    columnHelper.accessor("po_number", {
      cell: (context) => context.getValue(),
      header: "po number",
      id: "po_number",
      enableSorting: true,
    }),
    columnHelper.accessor("notes", {
      cell: (context) => context.getValue(),
      header: "notes",
      id: "notes",
      enableSorting: false,
    }),
    columnHelper.accessor("gross_amount_cents", {
      cell: formatPrice,
      header: "total",
      id: "amount",
      sortingFn: "alphanumeric",
      enableSorting: true,
    }),
    columnHelper.accessor("vat_amount_cents", {
      cell: formatPrice,
      header: t("spend.totalTax.label"),
      id: "vat_amount",
    }),
    columnHelper.accessor("outstanding_amount_cents", {
      cell: formatPrice,
      header: "amount due",
      id: "outstanding_amount_cents",
      enableSorting: true,
    }),
    columnHelper.accessor("payment_method", {
      cell: (context) => context.getValue(),
      header: "payment method",
      id: "payment_method",
      enableSorting: false,
    }),
    columnHelper.accessor("status", {
      cell: (context) => context.getValue(),
      header: "status",
      id: "status",
      enableSorting: false,
    }),
    columnHelper.accessor("accounting_integration_state", {
      cell: (context) => {
        const integrationState = context.getValue();
        if (!integrationState) {
          return "n/a";
        }

        if (integrationState === "true") {
          return "success";
        }

        return "failed";
      },
      header: "accounting",
      id: "accounting_integration_state",
      enableSorting: true,
    }),
    columnHelper.accessor("due_date", {
      cell: (context) => {
        const dueDate = context.getValue();
        if (!dueDate) {
          return "";
        }
        return format(new Date(dueDate), "dd MMM yyyy");
      },
      header: "due date",
      id: "due_date",
      sortingFn: "datetime",
      enableSorting: true,
    }),
    columnHelper.display({
      id: "filter",
      header: "filter",
    }),
  ];

  const columnIDs = defaultColumns.map((c) => c.id);
  const defaultColumnVisibility: Record<string, boolean> = {
    processed_date: false,
    invoice_number: false,
    vat_amount: false,
    outstanding_amount_cents: false,
    payment_method: false,
    due_date: false,
    accounting_integration_state: false,
  };
  const skeletonColumns = defaultColumns.filter((c) =>
    !c.id ? false : !(c.id in defaultColumnVisibility)
  );

  const skeletonRows = new Array(9).fill(null);
  const colCount = defaultColumns.length;

  const pagination = writable({
    offset: 0,
    limit: 150,
  });

  $: monthYearDividerFormat = isSameYear(
    $globalDatePickerStore.confirmedStartDate,
    $globalDatePickerStore.confirmedEndDate
  )
    ? "MMMM"
    : "MMMM yyyy";

  export let chatFiltersByID = createPersistedLocalStore<Record<string, boolean>>(
    CHAT_FILTER_STORAGE_KEY,
    {},
    CHAT_FILTER_VERSION
  );

  export let documentTypeFilters = createPersistedLocalStore<Record<string, boolean>>(
    DOCUMENT_TYPE_FILTER_STORAGE_KEY,
    {},
    DOCUMENT_TYPE_FILTER_VERSION
  );

  const columnVisibilityByID = createPersistedLocalStore<Record<string, boolean>>(
    INVOICE_COLUMN_FILTER_STORAGE_KEY,
    defaultColumnVisibility,
    INVOICE_COLUMN_FILTER_VERSION
  );

  $: if ($filteringDate) {
    columnVisibilityByID.update((colVis) => ({
      ...colVis,
      processed_date: $filteringDate === "processed_date" ? true : false,
    }));
  }

  $: minTypeColWidth = calculateMinTypeColWidth($transactionsQuery.data?.data);
  $: isEmptyData =
    $transactionsQuery.isSuccess &&
    $transactionsQuery.data?.data?.length === 0 &&
    hasInitialPageLoaded;

  $: if (hasInitializedFilters && $location === "/report") {
    syncQueryParamsWithFilters($querystring);
  }

  function calculateMinTypeColWidth(
    groupedTransactions: GroupedTransactions | undefined
  ): number | null {
    if (!groupedTransactions) {
      return null;
    }

    let hasCreditRequest = false;
    let hasCreditNote = false;

    for (const gt of groupedTransactions) {
      if (!gt.related_transactions) {
        continue;
      }

      for (const t of gt.related_transactions) {
        if (t.type === "credit_request") {
          hasCreditRequest = true;
        } else if (t.type === "creditnote") {
          hasCreditNote = true;
        }
      }

      if (hasCreditRequest) {
        break;
      }
    }

    if (hasCreditRequest) {
      return MIN_COL_WIDTH_CREDIT_REQUEST_TYPE;
    }

    if (hasCreditNote) {
      return MIN_COL_WIDTH_CREDIT_NOTE_TYPE;
    }

    return null;
  }

  function syncQueryParamsWithFilters(querystring: string | undefined) {
    const queryParams = new URLSearchParams(querystring);

    syncQueryParamsWithChatFilters(querystring, chatFiltersByID);
    syncQueryParmsWithDocumentTypeFilters(querystring, documentTypeFilters);

    if (queryParams.has("hidden-col")) {
      const hiddenColumns = queryParams.getAll("hidden-col");
      const columnVisibility: Record<string, boolean> = {};
      for (const id of hiddenColumns) {
        if (columnIDs.includes(id)) {
          columnVisibility[id] = false;
        }
      }
      columnVisibilityByID.set(columnVisibility);
    } else {
      columnVisibilityByID.set({});
    }
  }

  const chatFilterCount = derived(chatFiltersByID, (s) => Object.values(s).filter(Boolean).length);

  const chatFilters = derived(querystring, ($querystring) => {
    const queryParams = new URLSearchParams($querystring);
    return queryParams.getAll(CHAT_FILTER_PARAM_KEY);
  });

  const documentTypeFilterCount = derived(
    documentTypeFilters,
    (s) => Object.values(s).filter(Boolean).length
  );

  const documentFilters = derived(querystring, ($querystring) => {
    const queryParams = new URLSearchParams($querystring);
    return queryParams.getAll(DOCUMENT_TYPE_FILTER_PARAM_KEY);
  });

  const transactionQueryParams = derived(
    [
      selectedBuyer,
      globalDatePickerStore,
      filteringDate,
      pagination,
      chatFilters,
      documentFilters,
      querystring,
    ],
    ([
      $selectedBuyer,
      $globalDatePickerStore,
      $filteringDate,
      $pagination,
      $chatFilters,
      $documentFilters,
      $querystring,
    ]) => ({
      buyerID: $selectedBuyer?.id ?? "",
      startDate: $globalDatePickerStore.confirmedStartDate,
      endDate: $globalDatePickerStore.confirmedEndDate,
      filteringDate: $filteringDate,
      limit: $pagination.limit,
      page: { offset: $pagination.offset },
      chatFilters: $chatFilters,
      documentFilters: $documentFilters,
      linkStatusFilter: extractLinkStatusParam($querystring),
    })
  );

  const transactionsQuery = createGroupedTransactionsQuery(transactionQueryParams);

  const supplierChatsQueryParams = derived(
    [selectedBuyer, chatFiltersByID, transactionsQuery],
    ([$selectedBuyer, $chatFiltersByID, $transactionsQuery]) => {
      return {
        buyerIDs: [$selectedBuyer?.id ?? ""],
        chatIDs: difference(
          Object.keys($chatFiltersByID),
          $transactionsQuery.data?.chats?.map((c) => c.chat_id) ?? []
        ),
      };
    }
  );
  const supplierChatsUnavailableForDateRange =
    createSuppliersChatsByBuyerIDsQuery(supplierChatsQueryParams);

  $: if ($querystring && !supplierTooltipTimeout && $supplierFilterBadgeElement) {
    const queryParams = new URLSearchParams($querystring);
    if (queryParams.get("entry") === "overview") {
      isSupplierTooltipOpen = true;
      supplierTooltipTimeout = setTimeout(() => {
        isSupplierTooltipOpen = false;
      }, 7 * 1000);
    }
  }

  const pageInfo = derived([transactionsQuery, pagination], ([$transactionsQuery, $pagination]) => {
    if (!$transactionsQuery.data) {
      return {
        totalPages: 0,
        currentPage: 0,
      };
    }

    const totalPages = Math.ceil($transactionsQuery.data.total_items / $pagination.limit);

    return {
      totalPages,
      currentPage: Math.min(Math.ceil($pagination.offset / $pagination.limit) + 1, totalPages),
    };
  });

  $: if ($pageInfo.currentPage && !$transactionsQuery.isPlaceholderData) {
    gridRef?.scrollTo(0, 0);
  }

  const sorting = writable<ColumnSort[]>([]);

  function setSorting(updater: Updater<ColumnSort[]>) {
    if (updater instanceof Function) {
      sorting.set(updater(get(sorting)));
    } else {
      sorting.set(updater);
    }
  }

  const enrichedTransactions = derived(
    [transactionsQuery, sorting],
    ([$transactionsQuery, $sorting]) => {
      return enrichDisplayedTransactions($transactionsQuery.data?.data, $sorting);
    }
  );

  function enrichDisplayedTransactions(
    transactions: GroupedTransactions | undefined,
    _sorting: ColumnSort[] = []
  ): EnrichedGroupedTransaction[] {
    const enrichedTransactions: EnrichedGroupedTransaction[] = [];

    if (!transactions) {
      return enrichedTransactions;
    }

    transactions.forEach((t) => {
      const transformedTransaction: EnrichedGroupedTransaction = {
        ...t,
        related_transactions: t.related_transactions?.map((_t, i, list) => ({
          ..._t,
          isLastSubRowInGroup: i === list.length - 1,
        })),
      };

      enrichedTransactions.push(transformedTransaction);
    });

    return enrichedTransactions;
  }

  function shouldShowDate(row: Row<EnrichedGroupedTransaction>, index: number) {
    if (index === 0) {
      return true;
    }
    const previousRow = rows[index - 1];
    if (!previousRow) {
      return false;
    }
    const isSubRow = !!row.parentId;
    if (isSubRow) {
      return false;
    }
    if (
      row.original.delivery_date &&
      previousRow.original.delivery_date &&
      !isSameDay(new Date(row.original.delivery_date), new Date(previousRow.original.delivery_date))
    ) {
      return true;
    }

    return false;
  }

  const throttledUpdateHorizontalScrollShadow = rafThrottle(updateHorizontalScrollShadow);

  function handleGridScroll(
    e: UIEvent & {
      currentTarget: EventTarget & HTMLDivElement;
    }
  ) {
    throttledUpdateHorizontalScrollShadow(e.currentTarget.scrollLeft);
  }

  function updateHorizontalScrollShadow(scrollLeft: number) {
    if (scrollLeft > 0) {
      scrollGradientWidth = "40px";
    } else {
      scrollGradientWidth = "0px";
    }
  }

  function rafThrottle(cb: (...args: any[]) => void) {
    let requestID: number;

    return (...args: any[]) => {
      cancelAnimationFrame(requestID);
      requestID = requestAnimationFrame(() => {
        cb(...args);
      });
    };
  }

  function setColumnVisibility(updater: Updater<Record<string, boolean>>) {
    const columnVisibility = updater instanceof Function ? updater($columnVisibilityByID) : updater;
    const queryParams = new URLSearchParams($querystring);
    for (const [key, isVisible] of Object.entries(columnVisibility)) {
      if (isVisible) {
        // @ts-ignore For some odd reason svelte doesn't like the optional value param.
        queryParams.delete("hidden-col", key);
      } else {
        queryParams.append("hidden-col", key);
      }
    }
    if (queryParams.size > 0) {
      push(`${$location}?${queryParams.toString()}`);
    } else {
      push($location);
    }
  }

  function handleTransactionClick(transaction: Row<EnrichedGroupedTransaction>) {
    linkTransactionsModal.open(transaction.original);
  }

  const table = createSvelteTable(
    derived(
      [enrichedTransactions, columnVisibilityByID, sorting],
      ([$enrichedTransactions, $columnVisibilityByID, $sorting]) =>
        tableOptions({
          data: $enrichedTransactions,
          columns: defaultColumns,
          state: {
            columnVisibility: $columnVisibilityByID,
            sorting: $sorting,
          },
          onSortingChange: setSorting,
          onColumnVisibilityChange: setColumnVisibility,
          getCoreRowModel: getCoreRowModel(),
          getExpandedRowModel: getExpandedRowModel(),
          getSortedRowModel: getSortedRowModel(),
          getSubRows: (row) => row.related_transactions,
          getRowId: (row) => row.key,
          debugColumns: __DEV__,
          debugTable: __DEV__,
          debugHeaders: __DEV__,
          enableSortingRemoval: false,
        })
    )
  );

  function extractFormattedDateParts(
    cellAccessor: ColumnDefTemplate<CellContext<EnrichedGroupedTransaction, unknown>> | undefined,
    context: CellContext<EnrichedGroupedTransaction, unknown>
  ): [string, string] | null {
    if (typeof cellAccessor === "function") {
      return cellAccessor(context).split(" ");
    }

    return null;
  }

  let gridRef: HTMLDivElement | null = null;
  let dateCellRefs: HTMLDivElement[] = [];
  let observer: IntersectionObserver | null = null;
  let currentStickiedDateCell: HTMLDivElement | null = null;
  $: rows = $table.getRowModel().rows;
  $: rowIndexesByDisplayedDate = extractRowIndexesPerDisplayedDate(rows);

  $: scrollGradientWidth = "0px";

  const columnWidthsByID = new Map<string, string>([
    ["supplier_name", "minmax(max-content, 200px)"],
    ["type", "minmax(max-content, 80px)"],
    ["reference", "minmax(max-content, 120px)"],
    ["notes", "minmax(175px, 220px)"],
    ["payment_method", "minmax(max-content, 120px)"],
    ["status", "minmax(max-content, 80px)"],

    ["amount", "minmax(max-content, 70px)"],
    ["vat_amount", "minmax(max-content, 70px)"],
    ["amount", "minmax(max-content, 70px)"],
    ["outstanding_amount_cents", "minmax(max-content, 70px)"],

    ["delivery_date", "minmax(max-content, 100px)"],
    ["due_date", "minmax(max-content, 100px)"],

    ["date", "48px"],
    ["filter", "minmax(48px, 1fr)"],
  ]);

  $: visibleColumnWidths = getVisibleColumnWidths(
    $table.getAllLeafColumns(),
    $transactionsQuery.isLoading
  );

  $: filterableCols = getFilterableColumns($table.getAllLeafColumns());

  function getFilterableColumns(allCols: Column<EnrichedGroupedTransaction, unknown>[]) {
    const filterableCols: {
      general: Column<EnrichedGroupedTransaction, unknown>[];
      rekkiLine: Column<EnrichedGroupedTransaction, unknown>[];
    } = {
      general: [],
      rekkiLine: [],
    };

    allCols.forEach((c) => {
      if (["outstanding_amount_cents", "payment_method", "status", "due_date"].includes(c.id)) {
        filterableCols.rekkiLine.push(c);
      } else if (!["date", "supplier_name", "type", "filter"].includes(c.id)) {
        filterableCols.general.push(c);
      }
    });

    return filterableCols;
  }

  function getVisibleColumnWidths(
    columns: Column<EnrichedGroupedTransaction, unknown>[],
    isLoading: boolean
  ) {
    const colWidths: string[] = [];

    if (isLoading) {
      skeletonColumns.forEach((c) => {
        if (c.id) {
          colWidths.push(columnWidthsByID.get(c.id) ?? "1fr");
        }
      });
    } else {
      columns.forEach((c) => {
        if (c.getIsVisible()) {
          colWidths.push(columnWidthsByID.get(c.id) ?? "1fr");
        }
      });
    }

    return colWidths.join(" ");
  }

  function getMonthYearDivider(row: Row<EnrichedGroupedTransaction>, index: number) {
    const isSubRow = !!row.parentId;
    if (isSubRow) {
      return null;
    }

    const isFirstRow = index === 0;
    const prevRowDeliveryDate = rows[index - 1]?.original.delivery_date;
    const currentDate = new Date();
    const dateToCompare = isFirstRow
      ? currentDate
      : prevRowDeliveryDate
      ? new Date(prevRowDeliveryDate)
      : null;

    if (!dateToCompare) {
      return null;
    }

    if (!row.original.delivery_date) {
      return null;
    }

    const deliveryDate = new Date(row.original.delivery_date);

    if (!isSameMonth(deliveryDate, dateToCompare)) {
      return format(deliveryDate, monthYearDividerFormat);
    }

    return null;
  }

  function renderCellValue(cell: Cell<EnrichedGroupedTransaction, unknown>): string | null {
    if (typeof cell.column.columnDef.cell === "function") {
      return cell.column.columnDef.cell?.(cell.getContext());
    }
    return null;
  }

  function renderHeaderValue(header: Header<EnrichedGroupedTransaction, unknown>): string | null {
    if (typeof header.column.columnDef.header === "function") {
      return header.column.columnDef.header(header.getContext());
    }
    if (typeof header.column.columnDef.header === "string") {
      return header.column.columnDef.header;
    }
    return null;
  }

  function renderColumnDefHeader(column: Column<EnrichedGroupedTransaction, unknown>): string {
    if (typeof column.columnDef.header === "string") {
      return column.columnDef.header;
    }

    return "";
  }

  function handleDateCellStickiness(entries: IntersectionObserverEntry[]) {
    // skip initial load
    if (entries.length === dateCellRefs.length) {
      return;
    }

    entries.forEach((entry) => {
      const dataDate = entry.target.getAttribute("data-date");
      const rowIndex = entry.target.getAttribute("data-row");
      const isValidEntry = !dataDate || !rowIndex || entry.boundingClientRect.top > 400;

      if (isValidEntry) {
        return;
      }

      const normalizedDate = startOfDay(new Date(dataDate)).toISOString();
      if (!Array.isArray(rowIndexesByDisplayedDate[normalizedDate])) {
        return;
      }

      const [start, end] = rowIndexesByDisplayedDate[normalizedDate];
      const rowIndexNum = Number(rowIndex);
      const isInitialLoadEntry =
        currentStickiedDateCell === null &&
        rowIndexNum === start &&
        start !== end &&
        rowIndexNum === 0;

      if (isInitialLoadEntry) {
        currentStickiedDateCell = entry.target as HTMLDivElement;
        currentStickiedDateCell.classList.add("invoice-data-grid__grid-cell__sticky");
        return;
      }

      const shouldStick = Number(rowIndex) === start && entry.isIntersecting && start !== end;
      const shouldDetach = Number(rowIndex) === end && !entry.isIntersecting;

      if (shouldStick) {
        currentStickiedDateCell?.classList.remove("invoice-data-grid__grid-cell__sticky");
        currentStickiedDateCell = entry.target as HTMLDivElement;
        currentStickiedDateCell.classList.add("invoice-data-grid__grid-cell__sticky");
      } else if (shouldDetach) {
        currentStickiedDateCell?.classList.remove("invoice-data-grid__grid-cell__sticky");
      }
    });
  }

  $: gridObserverOptions = {
    root: gridRef,
    rootMargin: "-32px 0px 0px 0px",
    threshold: [0.85, 1],
  };

  onMount(() => {
    if (gridRef) {
      observer = new IntersectionObserver(handleDateCellStickiness, gridObserverOptions);
    }
    initializeFilters();
    hasInitializedFilters = true;
    initialPageLoadTimeout = setTimeout(() => {
      hasInitialPageLoaded = true;
    }, 250);
  });

  onDestroy(() => {
    if (observer) {
      observer.disconnect();
      observer = null;
    }
    if (supplierTooltipTimeout) {
      clearTimeout(supplierTooltipTimeout);
    }
    if (initialPageLoadTimeout) {
      clearTimeout(initialPageLoadTimeout);
    }
  });

  beforeUpdate(() => {
    if (observer && $transactionsQuery.isLoading) {
      observer.disconnect();
    }
  });

  afterUpdate(() => {
    if (gridRef) {
      observer = new IntersectionObserver(handleDateCellStickiness, gridObserverOptions);
    }

    if (observer) {
      for (const ref of dateCellRefs) {
        if (ref) {
          observer.observe(ref);
        }
      }
    }
  });

  function initializeFilters() {
    const queryParams = new URLSearchParams(get(querystring));
    const currentLocation = get(location);
    const chatFilters = get(chatFiltersByID);
    const documentFilters = get(documentTypeFilters);

    const columnVisibility = get(columnVisibilityByID);

    if (queryParams.has("supplier") || queryParams.has("hidden-col")) {
      return;
    }

    if (!isEmpty(chatFilters)) {
      const chatQueryParams = toChatFiltersQueryParams(chatFilters);

      for (const [key, supplierID] of chatQueryParams.entries()) {
        queryParams.append(key, supplierID);
      }
    }

    if (!isEmpty(documentFilters)) {
      const documentTypeQueryParams = toDocumentTypeFiltersQueryParams(documentFilters);

      for (const [key, value] of documentTypeQueryParams.entries()) {
        queryParams.append(key, value);
      }
    }

    if (!isEmpty(columnVisibility)) {
      for (const [key, isVisible] of Object.entries(columnVisibility)) {
        if (!isVisible) {
          queryParams.append("hidden-col", key);
        }
      }
    }

    if (queryParams.size) {
      replace(`${currentLocation}?${queryParams.toString()}`);
    }
  }

  function toChatFiltersQueryParams(chatFiltersByID: Record<string, boolean>) {
    const searchParams = new URLSearchParams();
    Object.entries(chatFiltersByID).forEach(([id, isFiltered]) => {
      if (isFiltered) {
        searchParams.append(CHAT_FILTER_PARAM_KEY, id);
      }
    });
    return searchParams;
  }

  function toDocumentTypeFiltersQueryParams(documentTypeFilters: Record<string, boolean>) {
    const searchParams = new URLSearchParams();
    Object.entries(documentTypeFilters).forEach(([type, isFiltered]) => {
      if (isFiltered) {
        searchParams.append(DOCUMENT_TYPE_FILTER_PARAM_KEY, type);
      }
    });
    return searchParams;
  }

  function extractRowIndexesPerDisplayedDate(rows: Row<EnrichedGroupedTransaction>[]) {
    const dateRowIndexes: { [key: string]: IndexRange } = {};
    rows?.forEach((r, i, list) => {
      const showDate = shouldShowDate(r, i);
      if (showDate && r.original.delivery_date) {
        dateRowIndexes[startOfDay(new Date(r.original.delivery_date)).toISOString()] = [
          r.index,
          r.index,
        ];
      } else if (
        !showDate &&
        list[i + 1] &&
        shouldShowDate(list[i + 1], i + 1) &&
        r.original.delivery_date
      ) {
        const d = dateRowIndexes[startOfDay(new Date(r.original.delivery_date)).toISOString()];
        if (d) {
          d[1] = r.index;
        }
      }
    });
    return dateRowIndexes;
  }

  function getCellGap(columnID: string, canExpand: boolean) {
    if (columnID === "supplier_name" && canExpand) {
      return {
        pl: "0px",
        pr: "12px",
      };
    }

    if (columnID !== "date") {
      return {
        pl: "12px",
        pr: "12px",
      };
    }

    return {
      pl: undefined,
      pr: undefined,
    };
  }

  function getSecondaryTransactionStatus(
    transactions: Transaction[] | undefined
  ): SecondaryStatus | null {
    if (!transactions) {
      return null;
    }

    const creditApplied = transactions.find(
      (t) =>
        t.type === "creditnote" &&
        ["creditnote", "creditnote_issued", "creditnote_diff_amount"].includes(t.status)
    );
    if (creditApplied) {
      return "creditApplied";
    }

    const reportedIssue = transactions.find(
      (t) => t.type === "checkin" || t.type === "credit_request"
    );
    if (reportedIssue) {
      return reportedIssue.is_resolved ? "resolved" : "unresolved";
    }

    return null;
  }

  function getStickyCellPosLeft(
    columnID: string,
    dateColWidth: number,
    supplierNameColWidth: number
  ) {
    return columnID === "supplier_name"
      ? `${dateColWidth}px`
      : columnID === "type"
      ? `${supplierNameColWidth + dateColWidth}px`
      : undefined;
  }

  function getExpandableEdge(
    columnID: string,
    isExpanded: boolean,
    isLastSubRowInGroup: boolean | undefined
  ): ExpandableEdge {
    if (isExpanded && columnID === defaultColumns[1].id) {
      return "top-start";
    }

    if (isExpanded && columnID === defaultColumns[defaultColumns.length - 1].id) {
      return "top-end";
    }

    if (isLastSubRowInGroup && columnID === defaultColumns[1].id) {
      return "bottom-start";
    }

    if (isLastSubRowInGroup && columnID === defaultColumns[defaultColumns.length - 1].id) {
      return "bottom-end";
    }

    return "none";
  }

  $: supplierFilterMenuItems = createChatFilterMenuItems(
    $transactionsQuery.data?.chats,
    $chatFiltersByID,
    $supplierChatsUnavailableForDateRange.data
  );

  $: documentTypeFilterMenuItems = createDocumentTypeFilterMenuItems($documentTypeFilters);
</script>

<div
  style:position="relative"
  style:--grid-width={GRID_WIDTH}
  style:--col-header-height={COL_HEADER_HEIGHT}
  style:--page-header-height="{$invoicesPageHeaderHeight}px"
  style:--overview-height="{$invoicesPageOverviewHeight}px"
  style:--scrollbar-width={SCROLL_BAR_WIDTH}
  style:--side-menu-width="{$sideMenuWidth}px"
  style:--pagination-control-height="{MAX_TABLE_PAGINATION_CONTROL_HEIGHT}px"
  style:height="100%"
>
  {#if $transactionsQuery.isPlaceholderData}
    <div class="shimmer-loader" />
  {/if}

  <div
    class="main-grid"
    class:paginates={$pageInfo.totalPages > 1}
    class:empty-grid={isEmptyData}
    role="grid"
    aria-colcount={colCount}
    style:grid-template-columns={visibleColumnWidths}
    style:--scroll-gradient-width={scrollGradientWidth}
    bind:this={gridRef}
    on:scroll={handleGridScroll}
  >
    {#each $table.getHeaderGroups() as headerGroup}
      <div class="header-row">
        {#if $transactionsQuery.isLoading || !hasInitialPageLoaded}
          {#each skeletonColumns as col (col.id)}
            <SkeletonColHeader colID={col.id ?? ""} />
          {/each}
        {:else}
          {#each headerGroup.headers as header (header.id)}
            {#if header.id === "date"}
              <HeaderCell
                edge="start"
                --sticky-top="0"
                --sticky-left="0"
                --z-index="2"
                --justify="center"
                on:click={header.column.getToggleSortingHandler()}
                bind:colWidth={dateColWidth}
              >
                <Icon
                  name={header.column.getIsSorted() === "asc" ? "calendarAsc" : "calendar"}
                  --w="16px"
                  --h="16px"
                  --display="block"
                  --color="var(--rk-text-low)"
                />
              </HeaderCell>
            {:else if header.id === "filter"}
              <HeaderCell
                edge="end"
                gradientFade="start"
                --sticky-top="0"
                --sticky-right="0"
                --z-index="2"
                --justify="flex-end"
              >
                <ButtonNew
                  {...$triggerAttrs}
                  size="xs"
                  variant="icon"
                  bind:element={$triggerElement}
                  on:click={onTriggerClick}
                  --icon-color={$isFilterMenuOpen ? "var(--rk-neutral-4)" : "var(--rk-text-low)"}
                  --icon-color-hover="var(--rk-neutral-4)"
                >
                  <Icon slot="icon" name="controls" />
                </ButtonNew>
              </HeaderCell>
            {:else if header.id === "supplier_name"}
              {@const headerValue = renderHeaderValue(header)}
              <HeaderCellFilter
                on:click={supplierFilterOnTriggerClick}
                bind:triggerElement={$supplierFilterTriggerElement}
                bind:colWidth={supplierNameColWidth}
                filterQueryParam={CHAT_FILTER_PARAM_KEY}
                stickyLeft={dateColWidth}
                badgeElement={supplierFilterBadgeElement}
                filterCount={$chatFilterCount}
                isHintTooltipVisible={isSupplierTooltipOpen}
                isFilterMenuOpen={$supplierFilterOpen}
                triggerAttrs={$supplierFilterTriggerAttrs}
                filterMenuAction={supplierFilterMenu}
                menuAttrs={$supplierFilterMenuAttrs}
                filterMenuItems={supplierFilterMenuItems}
                toggleMode="inclusive"
              >
                {headerValue}
              </HeaderCellFilter>
            {:else if header.id === "type"}
              {@const headerValue = renderHeaderValue(header)}
              <HeaderCellFilter
                on:click={documentTypeFilterOnTriggerClick}
                bind:triggerElement={$documentTypeFilterTriggerElement}
                filterQueryParam={DOCUMENT_TYPE_FILTER_PARAM_KEY}
                stickyLeft={supplierNameColWidth + dateColWidth}
                badgeElement={documentTypeFilterBadgeElement}
                filterCount={$documentTypeFilterCount}
                isFilterMenuOpen={$documentTypeFilterOpen}
                triggerAttrs={$documentTypeFilterTriggerAttrs}
                filterMenuAction={documentTypeFilterMenu}
                menuAttrs={$documentTypeFilterMenuAttrs}
                filterMenuItems={documentTypeFilterMenuItems}
                toggleMode="inclusive"
              >
                {headerValue}
              </HeaderCellFilter>
            {:else}
              {@const headerValue = renderHeaderValue(header)}
              <HeaderCell
                --sticky-top="0"
                --z-index="1"
                sortable={header.column.getCanSort()}
                sortDirection={header.column.getIsSorted()}
                on:click={header.column.getToggleSortingHandler()}
              >
                <TextNew
                  font="ocr"
                  size="m"
                  --rk-text-color="var(--rk-text-transparency-dark)"
                  --whitespace="nowrap"
                >
                  {headerValue}
                </TextNew>
              </HeaderCell>
            {/if}
          {/each}
        {/if}
      </div>
    {/each}

    <div class="body-grid" class:body-gap={$transactionsQuery.isLoading}>
      {#if $transactionsQuery.isLoading || !hasInitialPageLoaded}
        {#each skeletonRows as _, i}
          <InvoiceRowSkeleton isDateCellVisible={i % 4 === 0} />
        {/each}
      {:else if !isEmptyData}
        {#each rows as row, index (row.id)}
          {@const isParent = !Boolean(row.parentId)}
          {@const canClickRow = !$transactionsQuery.isPlaceholderData}
          {@const monthYearDivider = getMonthYearDivider(row, index)}
          {@const isNextRowExpanded = rows[index + 1]?.getIsExpanded()}
          {@const shouldShowUnlinked = showAsUnlinkedTransaction(row.original)}
          {@const orderingOnRekki =
            row.original.type === "order" || row.original.is_ordering_on_rekki}
          {@const shouldShowDivider =
            !isNextRowExpanded && !row.original.isLastSubRowInGroup && index !== rows.length - 1}
          {#if !!monthYearDivider}
            <div class="row">
              <div class="date-divider" style:left="{dateColWidth}px">
                <TextNew weight="medium" size="s">
                  {monthYearDivider}
                </TextNew>
              </div>
              <div class="date-divider-row-stub" />
            </div>
          {/if}
          <div
            class="row"
            role="row"
            class:clickable={canClickRow}
            tabindex={0}
            on:click={canClickRow ? () => handleTransactionClick(row) : undefined}
            on:keypress={canClickRow ? () => handleTransactionClick(row) : undefined}
          >
            {#each row.getVisibleCells() as cell (cell.id)}
              {#if cell.column.id === "date"}
                {@const dateParts = extractFormattedDateParts(
                  cell.column.columnDef.cell,
                  cell.getContext()
                )}
                {@const showDate = shouldShowDate(row, index)}
                <DateCell
                  day={showDate ? dateParts?.[0] : undefined}
                  month={showDate ? dateParts?.[1] : undefined}
                  date={isParent
                    ? cell.row.original.delivery_date
                    : cell.row.getParentRow()?.original.delivery_date}
                  rowIndex={row.index}
                  {dateCellRefs}
                />
              {:else}
                {@const cellValue = renderCellValue(cell)}
                {@const cellGap = getCellGap(cell.column.id, row.getCanExpand())}
                {@const isExpandableCell = row.getCanExpand() && cell.column.id === "supplier_name"}

                <BodyCell
                  --pl={cellGap.pl}
                  --pr={cellGap.pr}
                  --pos-left={getStickyCellPosLeft(
                    cell.column.id,
                    dateColWidth,
                    supplierNameColWidth
                  )}
                  --minW={cell.column.id === "type" && minTypeColWidth
                    ? `${minTypeColWidth}px`
                    : undefined}
                  sticky={cell.column.id === "supplier_name" || cell.column.id === "type"}
                  isExpanded={row.getIsExpanded() || row.getParentRow()?.getIsExpanded()}
                  expandableEdge={getExpandableEdge(
                    cell.column.id,
                    row.getIsExpanded(),
                    row.original.isLastSubRowInGroup
                  )}
                  columnID={cell.column.id}
                >
                  <svelte:fragment>
                    {#if isExpandableCell}
                      <ExpandableToggle
                        isExpanded={row.getIsExpanded()}
                        isHoverActive={expansionButtonHoveredByRowID.has(row.id)}
                        textColor={shouldShowUnlinked ? "error" : "neutral-4"}
                        on:click={(e) => {
                          e.stopPropagation();
                          row.getToggleExpandedHandler()();
                        }}
                        on:mouseover={() => {
                          if (!expansionButtonHoveredByRowID.has(row.id)) {
                            expansionButtonHoveredByRowID.set(row.id, true);
                            expansionButtonHoveredByRowID = expansionButtonHoveredByRowID;
                          }
                        }}
                        on:mouseleave={() => {
                          if (expansionButtonHoveredByRowID.has(row.id)) {
                            expansionButtonHoveredByRowID.delete(row.id);
                            expansionButtonHoveredByRowID = expansionButtonHoveredByRowID;
                          }
                        }}
                      />
                    {:else if shouldShowUnlinked && cell.column.id === "supplier_name"}
                      <div
                        class="icon-spacing"
                        class:cell-content={!expansionButtonHoveredByRowID.has(row.id)}
                      >
                        <ExclamationCircleIcon />
                      </div>
                    {:else if isParent && !shouldShowUnlinked && !orderingOnRekki && cell.column.id === "supplier_name"}
                      <div
                        class="icon-spacing"
                        class:cell-content={!expansionButtonHoveredByRowID.has(row.id)}
                        use:basicTooltip={{
                          title: "no orders on REKKI for this supplier",
                          textSize: "s",
                        }}
                      >
                        <FileOutlineIcon />
                      </div>
                    {:else if isParent && cell.column.id === "supplier_name"}
                      <div
                        class="icon-spacing"
                        class:cell-content={!expansionButtonHoveredByRowID.has(row.id)}
                      >
                        <div class="icon-spacer" />
                      </div>
                    {/if}

                    {#if (isParent || cell.column.id !== "supplier_name") && cellValue != null}
                      <div class:cell-content={!expansionButtonHoveredByRowID.has(row.id)}>
                        {#if cell.column.id === "status"}
                          <TransactionStatus
                            type={row.original.type}
                            status={cellValue}
                            secondaryStatus={row.getIsExpanded()
                              ? null
                              : getSecondaryTransactionStatus(row.original.related_transactions)}
                            dueDate={row.original.due_date}
                            isUnlinked={!!shouldShowUnlinked}
                          />
                        {:else if cell.column.id === "accounting_integration_state"}
                          <AccountingIntegrationStatus
                            status={isValidAccountingIntegrationStatus(cellValue)
                              ? cellValue
                              : "n/a"}
                          />
                        {:else}
                          <TextNew
                            size="xs"
                            --rk-text-color={shouldShowUnlinked
                              ? "var(--rekki-error)"
                              : "var(--rk-text-dark)"}
                            --overflow={cell.column.id === "notes" ? "hidden" : undefined}
                            overflow={cell.column.id === "notes" ? "lineClamp" : "initial"}
                          >
                            {cellValue}
                          </TextNew>
                        {/if}
                      </div>
                    {/if}
                  </svelte:fragment>
                </BodyCell>
              {/if}
            {/each}
          </div>

          {#if shouldShowDivider}
            <div
              class="row-divider-wrapper"
              class:divider-padding={!isParent || row.getIsExpanded()}
            >
              <div class="row-divider" />
            </div>
          {/if}

          {#if row.original.isLastSubRowInGroup && isNextRowExpanded}
            <div class="expanded-group-divider" />
          {/if}
        {/each}
      {/if}
    </div>
  </div>

  {#if isEmptyData}
    <div class="empty-state">
      <EmptyStateBasic
        title="no data for this period"
        subtitle="edit filters or try another date range"
      >
        <svelte:component this={ListIcon} slot="illustration" />
      </EmptyStateBasic>
    </div>
  {/if}

  <div
    class="pagination-controls"
    class:pagination-hidden={$pageInfo.totalPages <= 1}
    style:display={isEmptyData ? "none" : undefined}
  >
    <div>
      <ButtonNew
        --icon-color="var(--rk-neutral-4)"
        variant="icon"
        on:click={() => {
          const prev = $transactionsQuery.data?.prev;
          if (prev) {
            pagination.update((p) => ({ ...p, offset: prev.offset }));
          }
        }}
        disabled={$transactionsQuery.isPlaceholderData || !$transactionsQuery.data?.prev}
      >
        <Icon slot="icon" name="caretLeft" --w="24px" --h="24px" />
      </ButtonNew>
    </div>

    <div class="page-display">
      <TextNew size="xs" weight="medium" --whitespace="nowrap">
        {$pageInfo.currentPage}/{$pageInfo.totalPages}
      </TextNew>
    </div>
    <div>
      <ButtonNew
        --icon-color="var(--rk-neutral-4)"
        variant="icon"
        on:click={() => {
          const next = $transactionsQuery.data?.next;
          if (next) {
            pagination.update((p) => ({ ...p, offset: next.offset }));
          }
        }}
        disabled={$transactionsQuery.isPlaceholderData || !$transactionsQuery.data?.next}
      >
        <Icon slot="icon" name="caretLeft" direction="s" --w="24px" --h="24px" />
      </ButtonNew>
    </div>
  </div>
</div>

{#if $isFilterMenuOpen}
  <ContextMenu {...$menuAttrs} action={menu} --filter-menu-minW={FILTER_MENU_MIN_WIDTH}>
    <svelte:fragment>
      {#each filterableCols.general as generalCols (generalCols.id)}
        <FilterMenuItem
          title={renderColumnDefHeader(generalCols)}
          selected={generalCols.getIsVisible()}
          on:click={generalCols.getToggleVisibilityHandler()}
        />
      {/each}
      <div class="divider" />

      <div class="divider-header-container">
        <TextNew font="ocr" size="s" --rk-text-color="var(--rk-text-transparency-dark)">
          for lines paid through rekki
        </TextNew>
      </div>
      {#each filterableCols.rekkiLine as rekkiCols (rekkiCols.id)}
        <FilterMenuItem
          title={renderColumnDefHeader(rekkiCols)}
          selected={rekkiCols.getIsVisible()}
          on:click={rekkiCols.getToggleVisibilityHandler()}
        />
      {/each}
    </svelte:fragment>
  </ContextMenu>
{/if}

<style>
  .main-grid {
    display: grid;
    margin-top: var(--page-vertical-gap);
    padding-bottom: calc(var(--page-vertical-gap) * 2);
    overflow: auto;
    max-width: calc(100vw - var(--side-menu-width) - var(--content-px) * 2);
    max-height: calc(
      100vh - var(--page-header-height) - var(--overview-height) -
        (var(--page-vertical-padding) * 2) - 40px
    );
  }

  .empty-grid {
    overflow-x: hidden;
  }

  .main-grid.paginates {
    max-height: calc(
      100vh - var(--page-header-height) - var(--overview-height) - (var(--page-vertical-gap) * 3) -
        var(--scrollbar-width) - (var(--page-vertical-padding) * 2) - 40px
    );
  }

  .date-divider::after {
    content: "";
    position: absolute;
    top: 0;
    left: 100%;
    bottom: 0;
    display: block;
    width: var(--scroll-gradient-width);
    will-change: width;
    background: linear-gradient(
      270deg,
      rgba(0, 0, 0, 0) 0%,
      rgba(0, 0, 0, 0.01) 38.87%,
      rgba(0, 0, 0, 0.02) 60.71%,
      rgba(0, 0, 0, 0.03) 75.41%,
      rgba(0, 0, 0, 0.03) 90.08%,
      rgba(0, 0, 0, 0.04) 97.27%,
      rgba(0, 0, 0, 0.06) 99.46%,
      rgba(0, 0, 0, 0.11) 100%
    );
    transition: width 0.2s ease-in-out;
  }

  .body-grid {
    display: grid;
    grid-template-columns: subgrid;
    grid-column: 1 / -1;
  }

  .body-gap {
    margin-top: 8px;
  }

  .header-row {
    display: contents;
  }

  .icon-spacing {
    margin-right: 24px;
  }

  .icon-spacer {
    width: 16px;
  }

  .empty-state {
    width: 100%;
    height: 80vh;
    padding-bottom: 32px;
  }

  .date-divider {
    position: sticky;
    padding: 24px 0 8px 0;
    text-transform: lowercase;
    z-index: 0;
    grid-column: 2 / 4;
  }

  .date-divider-row-stub {
    grid-column: 4 / span lastline;
  }

  .row {
    display: contents;
  }

  .row:hover .cell-content {
    opacity: 0.5;
  }

  .clickable {
    cursor: pointer;
  }

  .row-divider-wrapper {
    grid-column: 2 / -1;
    background: var(--rk-neutral-0);
  }

  .row-divider {
    border-bottom-width: 1px;
    border-bottom-style: solid;
    border-color: var(--rk-divider-0);
  }

  .divider-padding {
    padding-left: 16px;
    padding-right: 16px;
  }

  .divider {
    margin: 4px 12px;
    border-top: 1px solid var(--rk-divider-0);
  }

  .divider-header-container {
    padding: 13px 12px;
  }

  .pagination-controls {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: 24px;
    max-height: var(--pagination-control-height);
  }

  .page-display {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 9px 8px 7px 8px;
    border-radius: 100px;
    border: 1px solid var(--rk-divider-0);
    min-width: 64px;
  }

  .pagination-hidden {
    visibility: hidden;
  }

  .shimmer-loader {
    position: absolute;
    top: var(--col-header-height);
    width: calc(var(--grid-width) - var(--scrollbar-width));
    bottom: calc(40px + var(--scrollbar-width));
    left: 0;
    overflow-x: hidden;
    z-index: 3;
    pointer-events: none;
  }

  .shimmer-loader::after {
    content: "";
    position: absolute;
    top: 0;
    right: -100%;
    bottom: 0;
    left: -100%;
    background-image: linear-gradient(
      to right,
      rgb(255 255 255 / 0) 33.3%,
      rgb(255 255 255 / 0.8),
      rgb(255 255 255 / 0) 66.6%
    );
    animation: shimmer 1.2s infinite cubic-bezier(0.42, 0, 0.5, 1);
  }

  @keyframes shimmer {
    from {
      transform: translateX(-33.3%);
    }
    to {
      transform: translateX(33.3%);
    }
  }

  .expanded-group-divider {
    grid-column: 2 / -1;
    height: 4px;
  }
</style>
